import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  View,
  TextInput,
  StyleSheet,
  Text,
  Platform,
  TouchableOpacity,
  type KeyboardTypeOptions,
  type TextInputProps,
} from "react-native";
import Colors from "../../../constants/colors";
import CloseIcon from "../../../../../../assets/images/CloseIcon.svg";
import { useTranslation } from "react-i18next";

const DOB_TEMPLATE = "mm/dd/yyyy";

type Props = {
  value?: string;
  isAutoFocus?: boolean;
  testID?: string;
  onChangeText: (text: string) => void;
  editable?: boolean;
  keyboardType?: KeyboardTypeOptions;
  autoCorrect?: boolean;
  spellCheck?: boolean;
  autoComplete?: TextInputProps["autoComplete"];
  maxLength?: number;
  errorText?: string | null;
  onClearError?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

/** Maps `formatDobInput` output onto the fixed `MM/DD/YYYY` template for grey remainder (handles 1-digit month/day). */
function splitDobTemplateDisplay(formatted: string): { black: string; grey: string } {
  const f = formatted ?? "";
  if (!f.trim()) {
    return { black: "", grey: DOB_TEMPLATE };
  }
  const trimmed = f.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return { black: trimmed, grey: "" };
  }
  if (f.includes("-")) {
    return { black: f, grey: "" };
  }

  let tb = 0;
  let fb = 0;
  let black = "";
  while (fb < f.length) {
    const c = f.charAt(fb);
    if (c === "/") {
      while (tb < DOB_TEMPLATE.length && DOB_TEMPLATE.charAt(tb) !== "/") {
        tb += 1;
      }
      if (tb < DOB_TEMPLATE.length && DOB_TEMPLATE.charAt(tb) === "/") {
        black += c;
        fb += 1;
        tb += 1;
      } else {
        black += f.slice(fb);
        return { black, grey: "" };
      }
    } else if (/[0-9]/.test(c)) {
      while (tb < DOB_TEMPLATE.length && DOB_TEMPLATE.charAt(tb) === "/") {
        tb += 1;
      }
      if (tb < DOB_TEMPLATE.length && /[mdy]/i.test(DOB_TEMPLATE.charAt(tb))) {
        black += c;
        fb += 1;
        tb += 1;
      } else {
        black += f.slice(fb);
        return { black, grey: "" };
      }
    } else {
      black += c;
      fb += 1;
    }
  }
  return { black, grey: DOB_TEMPLATE.slice(tb) };
}

const DOBTextInputComponent: React.FC<Props> = ({
  value,
  isAutoFocus,
  testID,
  onChangeText,
  editable = true,
  keyboardType = "number-pad",
  autoCorrect = false,
  spellCheck = false,
  autoComplete = "off" as TextInputProps["autoComplete"],
  maxLength = 10,
  errorText,
  onClearError,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
}) => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<TextInput>(null);
  const { black, grey } = splitDobTemplateDisplay(value ?? "");

  useLayoutEffect(() => {
    if (!isAutoFocus) return;
    inputRef.current?.focus();
  }, [isAutoFocus]);

  useEffect(() => {
    if (!isAutoFocus) return;
    const t = setTimeout(() => inputRef.current?.focus(), 120);
    const t2 = setTimeout(() => inputRef.current?.focus(), 400);
    return () => {
      clearTimeout(t);
      clearTimeout(t2);
    };
  }, [isAutoFocus]);

  useEffect(() => {
    if (Platform.OS === "web" && inputRef.current) {
      const node = (inputRef.current as any)?._node || inputRef.current;
      if (node && node instanceof HTMLElement) {
        node.style.caretColor = "#111";
      }
    }
  }, []);

  const borderColor = errorText
    ? Colors.errorRedColor
    : isFocused
      ? Colors.titleTextColor
      : Colors.textInputBackground;

  const hasValue = Boolean((value ?? "").trim().length > 0);
  const isCloseIconVisible = editable && (isFocused || (!!value && !!errorText));

  const handleChangeText = (text: string) => {
    if (errorText) onClearError?.();
    onChangeText(text);
  };

  const handleClear = () => {
    onChangeText("");
    if (errorText) onClearError?.();
    if (Platform.OS === "web") {
      setTimeout(() => inputRef.current?.focus(), 10);
    } else {
      inputRef.current?.focus();
    }
  };

  return (
    <View style={styles.outer}>
      <View style={styles.row}>
        <View style={[styles.container, { borderColor, borderWidth: 2 }]}>
          <View pointerEvents="none" style={StyleSheet.absoluteFillObject}>
            <Text style={styles.fixedLabel}>{t("DATE")}</Text>
          </View>

          <View
            pointerEvents="none"
            style={[styles.maskRow, hasValue && { paddingRight: 48 }]}
          >
            <Text style={styles.maskLine} numberOfLines={1}>
              <Text style={styles.maskTyped}>{black}</Text>
              <Text style={styles.maskGrey}>{grey}</Text>
            </Text>
          </View>

          <TextInput
            ref={inputRef}
            testID={testID}
            autoFocus={Boolean(isAutoFocus)}
            value={value}
            style={[
              styles.overlayInput,
              hasValue ? { paddingRight: 48 } : { paddingRight: 12 },
              Platform.OS === "web" &&
                ({
                  color: "transparent",
                  WebkitTextFillColor: "transparent",
                } as any),
              Platform.OS !== "web" && { color: "transparent" },
            ]}
            onFocus={() => {
              setIsFocused(true);
              onFocusProp?.();
            }}
            onBlur={() => {
              setIsFocused(false);
              onBlurProp?.();
            }}
            onChangeText={handleChangeText}
            editable={editable}
            keyboardType={keyboardType}
            autoCorrect={autoCorrect}
            spellCheck={spellCheck}
            autoComplete={autoComplete}
            maxLength={maxLength}
            cursorColor={Colors.black}
            selectionColor={Colors.black}
            caretHidden={false}
            importantForAutofill="no"
            textContentType="none"
            {...(Platform.OS === "web"
              ? ({
                  "data-lpignore": "true",
                  "data-1p-ignore": "true",
                  "data-bwignore": "true",
                  "data-form-type": "other",
                } as Record<string, string>)
              : {})}
          />

          {isCloseIconVisible ? (
            <TouchableOpacity
              testID="clearButton"
              style={styles.clearButton}
              onPress={handleClear}
              {...({ onMouseDown: (e: any) => e.preventDefault() } as any)}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <CloseIcon width={24} height={24} />
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
      {errorText ? <Text style={styles.errorText}>{errorText}</Text> : null}
    </View>
  );
};

export default DOBTextInputComponent;

const styles = StyleSheet.create({
  outer: {
    marginVertical: 8,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
  },
  container: {
    flex: 1,
    height: 56,
    borderRadius: 8,
    backgroundColor: Colors.textInputBackground,
    position: "relative",
    ...(Platform.OS === "web"
      ? ({
          borderStyle: "solid",
          outline: "none",
          transitionProperty: "border-color, border-width",
          transitionDuration: "150ms",
          transitionTimingFunction: "ease-in-out",
        } as any)
      : {}),
  },
  fixedLabel: {
    position: "absolute",
    left: 12,
    top: 6,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 400,
    color: Colors.subTitleColor,
    backgroundColor: "transparent",
    fontFamily: "Rubik",
  },
  maskRow: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: "flex-end",
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: Platform.OS === "web" ? 24 : 16,
    paddingBottom: 6,
  },
  maskLine: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: "Rubik",
    fontWeight: "400",
  },
  maskTyped: {
    color: Colors.titleTextColor,
    fontSize: 16,
    lineHeight: 24,
    fontFamily: "Rubik",
    fontWeight: "400",
  },
  maskGrey: {
    color: Colors.subTitleColor,
    fontSize: 16,
    lineHeight: 24,
    fontFamily: "Rubik",
    fontWeight: "400",
  },
  overlayInput: {
    ...StyleSheet.absoluteFillObject,
    paddingHorizontal: 12,
    paddingTop: Platform.OS === "web" ? 24 : 16,
    paddingBottom: 6,
    lineHeight: 24,
    fontSize: 16,
    fontWeight: "400",
    fontFamily: "Rubik",
    ...(Platform.OS === "web" &&
      ({
        outlineWidth: 0,
        outlineStyle: "none",
        border: "none",
        boxShadow: "none",
        backgroundColor: "transparent",
      } as any)),
  },
  errorText: {
    fontSize: 14,
    fontFamily: "Rubik",
    fontWeight: "400",
    lineHeight: 20,
    color: Colors.errorRedColor,
    marginTop: 4,
  },
  clearButton: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: [{ translateY: -10 }],
  },
});
